import { useQuery, useMutation } from "@apollo/client";
import { get, filter, map, isEmpty } from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";

import { TAGS_UI_COMPONENTS } from "src/consts/tags";

import useRefetchTagsOnPitChange from "src/components/TagsEditor/hooks/useRefetchTagsOnPitChange";
import useTagTypes from "src/components/TagsEditor/hooks/useTagTypes";
import {
  getTagsForEntity as getTagsForEntityQuery,
  addTagToEntity as addTagToEntityMutation,
  removeTagFromEntity as removeTagFromEntityMutation,
  addNewTagToEntity as addNewTagToEntityMutation,
} from "./queries/tags.graphql";
import TagSelectorContainer from "./sub-components/TagSelectorContainer";
import TagsEditor from "./TagsEditor";
import {
  modifyTags,
  makeComments,
  updateTagFragment,
  addTagToRemovedList,
  removeTagFromRemovedList,
  getTagTypesWithShowingHistory,
  transformEntityTagsForCache,
} from "./TagsEditor.util";

const TagsEditorContainer = ({
  entityId,
  entityName,
  updateCache,
  onEntityTagClick,
  readOnly,
  editOnly,
  manageEntityTags = true,
  presetEntityTags = [],
  tagTypesFilter,
  entityType,
  withAppliedCount,
  comments: commentsMetadata,
  groupType,
  mt,
  tagsVisibleIn = TAGS_UI_COMPONENTS.DIRECTORY,
  featureFlags,
  menuPlacement,
  excludingTagTypes = [],
  hideDropdown,
  onTagRemoved,
  onTagUpdated,
  onFinishEdit: externalOnFinishEdit,
}) => {
  const { tagTypesConfigForDisplay, tagTypesConfigForSelection } = useTagTypes({
    entityType,
    visibleIn: tagsVisibleIn,
    groupType,
    tagTypesFilter,
    excludingTagTypes,
  });

  // too many prop type warnings to see this as a prop type warning, so will have to be an exception
  if (!entityType) {
    throw new Error("TagsEditor entityType is missing");
  }

  const comments = useMemo(() => {
    return commentsMetadata
      ? {
          ...commentsMetadata,
          entity: {
            entityId,
            entityName,
            entityType,
          },
        }
      : undefined;
  }, [commentsMetadata, entityId, entityName, entityType]);

  // should this component manage tags or not (defaults to given value, set to true as soon as user hits edit button)
  const [currentManageEntityTags, setCurrentManageEntityTags] =
    useState(manageEntityTags);

  const tagTypesWithShowingHistory = getTagTypesWithShowingHistory(
    tagTypesConfigForDisplay
  );

  const {
    data: removedEntityData,
    loading: loadingRemovedEntityData,
    refetch: refetchRemovedTags,
  } = useQuery(getTagsForEntityQuery, {
    variables: {
      entityId,
      tagTypes: tagTypesWithShowingHistory,
      tagStatus: "REMOVED",
      entityType,
    },
    skip:
      !currentManageEntityTags ||
      !entityId ||
      isEmpty(tagTypesWithShowingHistory),
  });

  const removedTags = get(removedEntityData, "tags", []);
  const tagTypes = map(tagTypesConfigForSelection, "id");
  const displayTagTypes = tagTypesConfigForDisplay?.map((t) => t.id);

  // TODO : Replace this with a wrapper "ManagedTagsEditorContainer" that manages the entity for you
  // which can be optional
  // If there is presetTags passed in, it will be replaced by the response from the getTagsForEntities query
  // In this way, the update cache will work.
  // If outside of this editor, we need to update cache, then use the updateCache callback prop

  // NOTE: If variables are changed, updateTagsForEntityCache will need to be updated to handle the new variables.
  const {
    data: entityData,
    loading: loadingEntityTags,
    refetch: refetchEntityTags,
  } = useQuery(getTagsForEntityQuery, {
    variables: {
      entityId,
      tagTypes: displayTagTypes,
      entityType,
    },
    skip: !currentManageEntityTags || !entityId || !tagTypesConfigForSelection,
  });

  useRefetchTagsOnPitChange({
    entityId,
    entityType,
    currentManageEntityTags,
    tagTypesWithShowingHistory,
    displayTagTypes,
    tagTypesConfigForSelection,
    refetchRemovedTags,
    refetchEntityTags,
  });

  const loading = loadingEntityTags || loadingRemovedEntityData;

  const updateCacheAfterAddTag = (cache, { data: { result: entityTag } }) => {
    const cached = cache.readQuery({
      query: getTagsForEntityQuery,
      variables: { entityId, tagTypes: displayTagTypes, entityType },
    });

    // todo: in managed mode should always in the cache (see refactoring suggestion above)
    if (!cached) {
      return;
    }

    removeTagFromRemovedList({
      cache,
      entityId,
      tagTypesWithShowingHistory,
      tag: entityTag,
      addedTags: cached,
      replace: !!entityTag?.replace,
      entityType,
    });

    const { replace } = entityTag || {};
    const tags = modifyTags(cached.tags, entityTag, replace);
    cache.writeQuery({
      query: getTagsForEntityQuery,
      variables: { entityId, tagTypes: displayTagTypes, entityType },
      data: {
        tags,
      },
    });

    // Also update parent component cache if updateCache callback is provided
    // Transform EntityTag[] to the format expected by Person/Team fragment
    if (updateCache) {
      updateCache(transformEntityTagsForCache(tags));
    }
  };
  const [addTagToEntity] = useMutation(addTagToEntityMutation, {
    update: updateCacheAfterAddTag,
  });
  const [addNewTagToEntity] = useMutation(addNewTagToEntityMutation, {
    update: updateCacheAfterAddTag,
  });

  const [removeTagFromEntity] = useMutation(removeTagFromEntityMutation, {
    update(cache, { data: { result } }) {
      const cached = cache.readQuery({
        query: getTagsForEntityQuery,
        variables: { entityId, tagTypes: displayTagTypes, entityType },
      });

      if (!cached) {
        return;
      }

      addTagToRemovedList({
        tag: cached.tags.find((t) => t.tagId === result.tagId),
        cache,
        tagTypesWithShowingHistory,
        entityId,
        entityType,
      });

      const tags = filter(cached.tags, (r) => r.tagId !== result.tagId);

      cache.writeQuery({
        query: getTagsForEntityQuery,
        variables: { entityId, tagTypes: displayTagTypes, entityType },
        data: {
          tags,
        },
      });

      // Update tag fragment
      updateTagFragment(cache, result);

      // Also update parent component cache if updateCache callback is provided
      // Transform EntityTag[] to the format expected by Person/Team fragment
      if (updateCache) {
        updateCache(transformEntityTagsForCache(tags));
      }
    },
  });

  const setNewTag = useCallback(
    async (newValues, selectedTag) => {
      const {
        id: tagId,
        isUserCreatedTagValue,
        displayValue,
        attributes,
      } = newValues;

      const { id: tagType, allowMultipleOnEntity } = selectedTag;

      const tag = { tagId, tagType, displayValue };

      if (isUserCreatedTagValue) {
        await addNewTagToEntity({
          variables: {
            entityType,
            entityId,
            tagType,
            displayValue,
            attributes,
            replace: !allowMultipleOnEntity,
            ...makeComments(comments, tag),
          },
        });
      } else {
        await addTagToEntity({
          variables: {
            entityId,
            entityType,
            tagId,
            replace: !allowMultipleOnEntity,
            ...makeComments(comments, tag),
          },
        });
      }
    },
    [addNewTagToEntity, entityId, comments, addTagToEntity, entityType]
  );

  const removeTag = useCallback(
    async ({ tagId, type: tagType, displayValue }) => {
      const tag = { tagId, tagType, displayValue };

      if (!tagId) {
        return;
      }

      const response = await removeTagFromEntity({
        variables: {
          entityId,
          entityType,
          tagId,
          ...makeComments(comments, tag),
        },
      });

      // Call the callback if provided
      if (onTagRemoved && response?.data?.result) {
        onTagRemoved(response.data.result.tagId);
      }
    },
    [removeTagFromEntity, entityId, entityType, comments, onTagRemoved]
  );

  const existingTags = currentManageEntityTags
    ? get(entityData, "tags")
    : presetEntityTags;

  const onStartEdit = useCallback(() => {
    // as soon as we start editing, need to go back to managing the entity tags via our own queries rather than what is passed in.
    setCurrentManageEntityTags(true);
  }, []);

  const onFinishEdit = useCallback(() => {
    if (updateCache) {
      updateCache(existingTags);
    }
    if (externalOnFinishEdit) {
      externalOnFinishEdit();
    }
  }, [updateCache, existingTags, externalOnFinishEdit]);

  const handleOnClickEntityTag = (value) => {
    onEntityTagClick(value);
  };

  const combinedTags = [...(existingTags || []), ...(removedTags || [])];

  return (
    <TagsEditor
      readOnly={readOnly}
      editOnly={editOnly}
      existingTags={combinedTags}
      setNewTag={setNewTag}
      removeTag={removeTag}
      onStartEdit={onStartEdit}
      onFinishEdit={onFinishEdit}
      loading={loading}
      tagTypesConfig={tagTypesConfigForSelection}
      tagTypesConfigForDisplay={tagTypesConfigForDisplay}
      tagTypes={tagTypes}
      onEntityTagClick={handleOnClickEntityTag}
      withAppliedCount={withAppliedCount}
      comments={comments}
      mt={mt}
      components={{ TagSelector: TagSelectorContainer }}
      entityId={entityId}
      featureFlags={featureFlags}
      entityType={entityType}
      menuPlacement={menuPlacement}
      hideDropdown={hideDropdown}
      onTagUpdated={onTagUpdated}
    />
  );
};

TagsEditorContainer.propTypes = {
  entityId: PropTypes.string.isRequired,
  entityName: PropTypes.string,
  entityType: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  editOnly: PropTypes.bool,
  updateCache: PropTypes.func,
  onEntityTagClick: PropTypes.func,
  presetEntityTags: PropTypes.array,
  tagTypesFilter: PropTypes.array,
  manageEntityTags: PropTypes.bool,
  comments: PropTypes.object,
  groupType: PropTypes.string,
  withAppliedCount: PropTypes.bool,
  mt: PropTypes.string,
  tagsVisibleIn: PropTypes.string,
  featureFlags: PropTypes.object,
  menuPlacement: PropTypes.string,
  excludingTagTypes: PropTypes.array,
  hideDropdown: PropTypes.bool,
  onTagRemoved: PropTypes.func,
  onTagUpdated: PropTypes.func,
  onFinishEdit: PropTypes.func,
};

export default TagsEditorContainer;
